import React, { useEffect, useState } from "react";
import { Button, Modal, ProgressBar } from "react-bootstrap";

// Above this many candidates, "Vet visible" confirms first — vetting runs
// the AI over each candidate, so a large set takes a while and spends AI
// budget. Not a hard cap (large vets are a legitimate use); just a
// speed-bump. This is a *cost/time* warning, distinct from the *search*-
// side pool-size guardrail in the picker-perf-review-at-scale follow-up
// (which is about search latency). Tunable; calibrate against observed
// vet p50/p95 once there's production data.
export const VET_VISIBLE_WARN_THRESHOLD = 200;

function dedupeById(rows) {
  const seen = new Set();
  return (rows || []).filter((r) => {
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });
}

// Whimsy for the (~30s) vetting wait. A line-up of "reporters" the AI is
// sizing up: faces bob, verdict pips pop in a left-to-right wave, a light
// shimmer sweeps across. Purely decorative (aria-hidden); the count +
// elapsed timer carry the real status. Animations respect reduced-motion.
const VET_PIP_COLOURS = ["#198754", "#198754", "#ffc107", "#198754", "#dc3545"]; // good/good/ok/good/bad
const VET_MESSAGES = [
  "Reading the room…",
  "Sizing up the press corps…",
  "Checking who covers this beat…",
  "Weighing each one's fit…",
  "Sorting the keen from the maybe…",
  "Consulting the AI…",
];
const VET_ANIM_CSS = `
  .vet-stage { position: relative; display: inline-flex; gap: 0.55rem; padding: 0.4rem 1rem; overflow: hidden; }
  .vet-face { position: relative; font-size: 1.7rem; color: var(--bs-secondary, #6c757d); }
  .vet-pip {
    position: absolute; top: -3px; right: -5px; width: 10px; height: 10px;
    border-radius: 50%; border: 1.5px solid var(--bs-body-bg, #fff); transform: scale(0);
  }
  .vet-shimmer {
    position: absolute; top: 0; bottom: 0; left: -60px; width: 50px; pointer-events: none;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.7), transparent);
  }
  @media (prefers-reduced-motion: no-preference) {
    .vet-face { animation: vet-bob 1.7s ease-in-out infinite; }
    .vet-pip { animation: vet-pip-pop 2.4s ease-in-out infinite; }
    .vet-shimmer { animation: vet-shimmer 2.4s ease-in-out infinite; }
  }
  @keyframes vet-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
  @keyframes vet-pip-pop { 0%,100% { transform: scale(0); opacity: 0; } 25%,75% { transform: scale(1); opacity: 1; } }
  @keyframes vet-shimmer { 0% { left: -60px; } 100% { left: 100%; } }
`;

// The "Vet visible" control for the results column's bottom toolbar: the
// button, the big-count confirmation, the failed-batch retry, and the
// blocking in-progress modal.
//
// Vetting blocks the UI behind a modal with a themed animation + a
// ticking elapsed timer. A single ≤200-candidate batch is one OpenAI
// call that can sit ~30s with no incremental output, so a static inline
// bar reads as frozen ("is it broken?"); the modal makes "something is
// happening" unmistakable and stops the admin wandering off mid-vet.
export default function VetToolbar({ vettableRows, vetting, progress, failedBatches, onVetVisible, onCancelVet }) {
  const [showConfirm, setShowConfirm] = useState(false);
  // Candidates awaiting confirmation (set when a request is over the
  // threshold) — lets "Vet visible" and the failed-batch Retry share it.
  const [pendingVet, setPendingVet] = useState(null);
  // Elapsed seconds while vetting. A single ≤200 batch is one opaque
  // call with no incremental progress, so a static bar reads as frozen;
  // a ticking timer + a moving bar make "it's working" unmistakable.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!vetting) {
      setElapsed(0);
      return undefined;
    }
    const start = Date.now();
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [vetting]);

  const requestVet = (candidates) => {
    const unique = dedupeById(candidates);
    if (unique.length === 0) return;
    if (unique.length > VET_VISIBLE_WARN_THRESHOLD) {
      setPendingVet(unique);
      setShowConfirm(true);
    } else {
      onVetVisible?.(unique);
    }
  };
  const confirmVet = () => {
    setShowConfirm(false);
    if (pendingVet) onVetVisible?.(pendingVet);
    setPendingVet(null);
  };
  const cancelConfirm = () => {
    setShowConfirm(false);
    setPendingVet(null);
  };

  const failedCount = failedBatches.reduce((sum, b) => sum + (b.candidates?.length || 0), 0);
  const pendingCount = pendingVet?.length ?? 0;
  // Determinate only once batches are actually completing (multi-batch
  // runs). Otherwise show a full striped-animated bar — an indeterminate
  // "working" signal rather than a frozen 0%.
  const determinate = progress.completed > 0 && progress.completed < progress.total;
  const pct = determinate ? Math.round((progress.completed / progress.total) * 100) : 100;
  const message = VET_MESSAGES[Math.floor(elapsed / 3) % VET_MESSAGES.length];

  let vetButtonTitle = "Run AI vetting over the visible recipients";
  if (vettableRows.length === 0) {
    vetButtonTitle = "Every visible recipient already has a current AI verdict";
  }

  return (
    <>
      <div className="d-flex flex-column align-items-end gap-1">
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => requestVet(vettableRows)}
          disabled={vetting || vettableRows.length === 0}
          title={vetButtonTitle}
        >
          <i className="fa-solid fa-wand-magic-sparkles me-1" aria-hidden="true" />
          Vet visible{vettableRows.length > 0 ? ` (${vettableRows.length})` : ""}
        </Button>
        {failedCount > 0 && !vetting && (
          <div className="small text-danger d-flex align-items-center gap-2" role="alert">
            <span>
              <i className="fa-solid fa-triangle-exclamation me-1" aria-hidden="true" />
              AI vet failed for {failedCount} recipient{failedCount === 1 ? "" : "s"}.
            </span>
            <Button
              size="sm"
              variant="outline-danger"
              className="py-0"
              onClick={() => requestVet(failedBatches.flatMap((b) => b.candidates || []))}
            >
              Retry
            </Button>
          </div>
        )}
      </div>

      {/* Big-count confirmation — a cost/time speed-bump, not a hard cap. */}
      <Modal show={showConfirm} onHide={cancelConfirm} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">Vet {pendingCount} recipients?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This runs the AI over each of the {pendingCount} recipients. It can take a little while and uses AI budget.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={cancelConfirm}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmVet}>
            Vet {pendingCount}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Blocking in-progress modal. backdrop=static + keyboard=false so it
          can only be dismissed via Cancel; the line-up animation + ticking
          timer + rotating status keep a long single-batch wait from
          reading as frozen. */}
      <Modal show={vetting} backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center py-4">
          <style>{VET_ANIM_CSS}</style>
          <div className="vet-stage mb-3" aria-hidden="true">
            {VET_PIP_COLOURS.map((colour, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span className="vet-face" key={i} style={{ animationDelay: `${i * 0.12}s` }}>
                <i className="fa-solid fa-user-tie" />
                <span className="vet-pip" style={{ backgroundColor: colour, animationDelay: `${i * 0.28}s` }} />
              </span>
            ))}
            <span className="vet-shimmer" />
          </div>
          <div aria-live="polite">
            <div className="fw-semibold mb-1">
              Vetting {progress.total} recipient{progress.total === 1 ? "" : "s"} with AI…
            </div>
            <div className="text-muted small">
              {progress.completed > 0 ? `${progress.completed} done · ` : ""}
              {elapsed}s elapsed — this can take up to a minute.
            </div>
          </div>
          <div className="text-primary small fst-italic mt-2" aria-hidden="true" style={{ minHeight: "1.2em" }}>
            {message}
          </div>
          {determinate && (
            <ProgressBar
              now={pct}
              animated
              striped
              style={{ height: 5 }}
              className="mt-3"
              aria-label="AI vet progress"
            />
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="outline-secondary" onClick={onCancelVet}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
